/**
 * WD header: scroll down → main header scrolls away, only .wd-header__search-row is sticky.
 * Scroll up → full section sticky (header + search). Requires data-wd-compact-scroll.
 */
(function () {
  const stickyHeader = document.querySelector('sticky-header[data-wd-compact-scroll]');
  if (!stickyHeader) return;

  const section = document.querySelector('.shopify-section-header');
  if (!section) return;

  const searchRow = stickyHeader.querySelector('.wd-header__search-row');
  const mainHeader = stickyHeader.querySelector('.header');

  document.body.classList.add('wd-compact-scroll-enabled');

  let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
  let lastCompact = section.classList.contains('wd-header--compact');
  const pageTopY = 10;
  /** Below this, keep full header even while sticky */
  const compactMinY = 24;
  /** Accumulated px in one direction before toggling (trackpad sends many 1px events) */
  const downToCompact = 6;
  const upToExpand = 14;
  let scrollDownAccum = 0;
  let scrollUpAccum = 0;
  /** Brief pause after compact/expand so height change does not flip direction */
  let toggleLockUntil = 0;
  let wasFooterInView = false;

  function isFooterInView() {
    const footers = document.querySelectorAll('.shopify-section-wd-footer, .shopify-section-footer');
    const viewBottom = window.innerHeight;
    for (let i = 0; i < footers.length; i++) {
      const rect = footers[i].getBoundingClientRect();
      if (rect.top < viewBottom && rect.bottom > 0) {
        return true;
      }
    }
    return false;
  }

  function updateSearchAtFooter() {
    const hide =
      section.classList.contains('wd-header--compact') && isFooterInView();
    section.classList.toggle('wd-header--search-at-footer', hide);
  }

  function syncCompactSearchPin() {
    const pinned =
      section.classList.contains('wd-header--compact') &&
      !section.classList.contains('wd-header--search-at-footer');
    document.body.classList.toggle('wd-compact-search-pinned', pinned);
    if (searchRow && pinned) {
      document.documentElement.style.setProperty(
        '--wd-compact-search-height',
        `${searchRow.offsetHeight}px`
      );
    } else {
      document.documentElement.style.removeProperty('--wd-compact-search-height');
    }
  }

  function isMenuOpen() {
    return (
      section.classList.contains('header-menu-open') ||
      document.body.classList.contains('menu-mobile--open') ||
      document.body.classList.contains('menu-mobile--opening') ||
      document.body.classList.contains('menu-drawer--open') ||
      document.body.classList.contains('menu-drawer--opening')
    );
  }

  /**
   * Page scroll position where the search row reaches the top of the viewport in normal flow
   * (bottom of the main header). Only measurable while the section is not stuck to the top.
   */
  let compactThreshold = 0;
  /** Page scroll position of the header section's own place */
  let sectionTop = 0;
  /** Main header height in its normal (not header-scrolled) layout */
  let normalHeaderHeight = 0;
  /** Height header-scrolled takes off the main header (desktop nav row) */
  let collapsedHeight = 0;

  function updateCompactThreshold(scrollTop) {
    if (!mainHeader || section.classList.contains('shopify-section-header-sticky')) return;
    compactThreshold = mainHeader.getBoundingClientRect().bottom + scrollTop;
    sectionTop = section.getBoundingClientRect().top + scrollTop;
  }

  /**
   * header-scrolled hides the desktop nav row. The section keeps that height as margin-bottom
   * so the page content never jumps when the header collapses or expands.
   */
  function syncCollapsedHeight() {
    if (!mainHeader) return;
    if (!section.classList.contains('header-scrolled')) {
      normalHeaderHeight = mainHeader.offsetHeight;
      document.documentElement.style.removeProperty('--wd-header-collapsed-height');
      return;
    }
    collapsedHeight = Math.max(0, normalHeaderHeight - mainHeader.offsetHeight);
    document.documentElement.style.setProperty('--wd-header-collapsed-height', `${collapsedHeight}px`);
  }

  function onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const delta = scrollTop - lastScrollTop;
    updateCompactThreshold(scrollTop);
    // Restore the normal header where its bottom lines up with the collapsed sticky header's bottom
    const resetY = Math.max(pageTopY, sectionTop + collapsedHeight);

    // Back at the header's place the desktop hamburger is gone, so close its menu
    if (scrollTop <= resetY) section.classList.remove('header-menu-open');

    if (isMenuOpen()) {
      section.classList.remove('wd-header--compact', 'wd-header--search-at-footer');
      scrollDownAccum = 0;
      scrollUpAccum = 0;
      lastScrollTop = scrollTop;
      return;
    }

    if (scrollTop <= resetY) {
      scrollDownAccum = 0;
      scrollUpAccum = 0;
      wasFooterInView = false;
      section.classList.remove(
        'wd-header--compact',
        'header-scrolled',
        'wd-header--search-at-footer'
      );
      document.body.classList.remove('wd-compact-search-pinned');
      document.documentElement.style.removeProperty('--wd-compact-search-height');
      section.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
      document.body.classList.remove(
        'shopify-body-section-header-sticky',
        'shopify-body-section-header-hidden'
      );
    } else {
      section.classList.remove('shopify-section-header-hidden');
      document.body.classList.remove('shopify-body-section-header-hidden');

      const footerInView = isFooterInView();

      if (footerInView) {
        // Footer on screen: header scrolls away in flow and search row is hidden (see updateSearchAtFooter)
        section.classList.add('wd-header--compact', 'header-scrolled');
        section.classList.remove('shopify-section-header-sticky');
        document.body.classList.remove('shopify-body-section-header-sticky');
        scrollDownAccum = 0;
        scrollUpAccum = 0;
        wasFooterInView = true;
      } else if (wasFooterInView) {
        // Footer just left the viewport (scrolling up): bring back header + search right away
        wasFooterInView = false;
        section.classList.remove('wd-header--compact');
        section.classList.add('shopify-section-header-sticky', 'header-scrolled');
        document.body.classList.add('shopify-body-section-header-sticky');
        scrollDownAccum = 0;
        scrollUpAccum = 0;
      }

      const bypassLockNavExpand =
        !footerInView &&
        delta < 0 &&
        section.classList.contains('wd-header--compact') &&
        mainHeader &&
        mainHeader.getBoundingClientRect().bottom > 0;

      if (!footerInView && (Date.now() >= toggleLockUntil || bypassLockNavExpand)) {
        if (delta > 0) {
          scrollDownAccum += delta;
          scrollUpAccum = 0;
          if (
            scrollDownAccum >= downToCompact &&
            scrollTop > compactMinY &&
            // Pin only once the page is scrolled past the search row's place under the main header
            scrollTop >= compactThreshold
          ) {
            section.classList.add('wd-header--compact', 'header-scrolled');
            section.classList.remove('shopify-section-header-sticky');
            document.body.classList.remove('shopify-body-section-header-sticky');
            scrollDownAccum = 0;
            scrollUpAccum = 0;
          }
        } else if (delta < 0) {
          scrollUpAccum += -delta;
          scrollDownAccum = 0;
          const navBottom = mainHeader
            ? mainHeader.getBoundingClientRect().bottom
            : 0;
          const shouldExpand =
            scrollUpAccum >= upToExpand ||
            (section.classList.contains('wd-header--compact') && navBottom > 0);
          if (shouldExpand) {
            section.classList.remove('wd-header--compact');
            section.classList.add('shopify-section-header-sticky', 'header-scrolled');
            document.body.classList.add('shopify-body-section-header-sticky');
            scrollDownAccum = 0;
            scrollUpAccum = 0;
          }
        }
      }

      if (section.classList.contains('wd-header--compact')) {
        section.classList.remove('shopify-section-header-sticky');
        document.body.classList.remove('shopify-body-section-header-sticky');
      }
    }

    const compact = section.classList.contains('wd-header--compact');
    if (compact !== lastCompact) {
      toggleLockUntil = Date.now() + 250;
      scrollDownAccum = 0;
      scrollUpAccum = 0;
      lastScrollTop = scrollTop;
      lastCompact = compact;
    }

    updateSearchAtFooter();
    syncCompactSearchPin();
    syncCollapsedHeight();

    lastScrollTop = scrollTop;

    if (typeof setHeaderBottomPosition === 'function') {
      setHeaderBottomPosition();
    }

    if (scrollTop > pageTopY && !isMenuOpen()) {
      section.classList.remove(
        'shopify-section-header-hidden',
        'animate'
      );
      document.body.classList.remove('shopify-body-section-header-hidden');
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('load', onScroll);
  onScroll();
})();
